import React, { useState, useEffect, useRef } from 'react';
import { Check, ChevronDown, X, Search, Plus } from 'lucide-react';
import { Customer } from '../../../services/customerService';

interface CustomerMultiSelectProps {
  label: string;
  customers: Customer[];
  selectedCustomerIds: string[];
  onChange: (selectedIds: string[]) => void;
  disabled?: boolean;
  error?: string;
  isLoading?: boolean;
  onAddNewCustomer?: () => void;
}

const CustomerMultiSelect: React.FC<CustomerMultiSelectProps> = ({
  label,
  customers,
  selectedCustomerIds,
  onChange,
  disabled = false,
  error,
  isLoading = false,
  onAddNewCustomer,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Filter customers based on search term (search by ID/code, name, and company name)
  const filteredCustomers = customers.filter(customer => {
    const searchLower = searchTerm.toLowerCase();
    return (
      customer.code.toLowerCase().includes(searchLower) ||
      customer.name.toLowerCase().includes(searchLower) ||
      (customer.companyName && customer.companyName.toLowerCase().includes(searchLower))
    );
  });

  const handleToggle = (customerId: string) => {
    if (disabled) return;
    
    const newSelected = selectedCustomerIds.includes(customerId)
      ? selectedCustomerIds.filter(id => id !== customerId)
      : [...selectedCustomerIds, customerId];
    
    onChange(newSelected);
  };

  const handleToggleSelectAll = () => {
    if (disabled) return;
    const filteredIds = filteredCustomers.map(c => c._id);
    const allFilteredSelected = filteredIds.length > 0 && 
      filteredIds.every(id => selectedCustomerIds.includes(id));
    
    if (allFilteredSelected) {
      // Deselect all filtered customers
      onChange(selectedCustomerIds.filter(id => !filteredIds.includes(id)));
    } else {
      // Select all filtered customers
      onChange([...new Set([...selectedCustomerIds, ...filteredIds])]);
    }
  };

  const handleRemove = (customerId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (disabled) return;
    onChange(selectedCustomerIds.filter(id => id !== customerId));
  };

  const selectedCustomers = customers.filter(c => selectedCustomerIds.includes(c._id));
  const allFilteredSelected = filteredCustomers.length > 0 && 
    filteredCustomers.every(c => selectedCustomerIds.includes(c._id));

  return (
    <div className="space-y-2">
      <label className="block text-sm font-semibold text-gray-700 mb-2">
        {label} *
      </label>
      
      <div className="flex items-start gap-3">
        <div className="relative flex-1" ref={dropdownRef}>
        {/* Selected items display */}
        <div
          onClick={() => !disabled && setIsOpen(!isOpen)}
          className={`h-auto min-h-[48px] w-full rounded-xl border bg-white px-4 py-2 text-base text-gray-800 shadow-sm cursor-pointer focus:outline-none focus:ring-2 focus:ring-offset-0 transition-all duration-200 ${
            error
              ? "border-red-300 focus:border-red-500 focus:ring-red-500/20"
              : disabled
              ? "border-gray-200 bg-gray-50 cursor-not-allowed"
              : "border-gray-300 focus:border-brand-500 focus:ring-brand-500/20"
          }`}
        >
          <div className="flex flex-wrap gap-2 items-center">
            {selectedCustomers.length > 0 ? (
              selectedCustomers.map(customer => (
                <span
                  key={customer._id}
                  className="inline-flex items-start gap-1.5 px-3 py-1.5 bg-blue-50 text-blue-700 rounded-lg text-sm border border-blue-200"
                >
                  <div className="flex-1 min-w-0">
                    <div className="text-[12px] leading-tight font-medium">
                    {customer.companyName}
                    </div>
                    {customer.companyName && (
                      <div className="text-[10px] text-blue-600 mt-0.5">                      {customer.code} - {customer.name}
</div>
                    )}
                  </div>
                  {!disabled && (
                    <button
                      type="button"
                      onClick={(e) => handleRemove(customer._id, e)}
                      className="hover:bg-blue-100 rounded-full p-0.5 transition-colors flex-shrink-0 mt-0.5"
                    >
                      <X size={14} className="text-blue-600" />
                    </button>
                  )}
                </span>
              ))
            ) : (
              <span className="text-gray-400 py-1">Select customers...</span>
            )}
            {!disabled && (
              <ChevronDown
                size={20}
                className={`ml-auto text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
              />
            )}
          </div>
        </div>

        {/* Dropdown */}
        {isOpen && !disabled && (
          <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-xl shadow-lg max-h-80 overflow-hidden flex flex-col">
            {/* Search input */}
            <div className="p-3 border-b border-gray-200">
              <div className="relative">
                <Search size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by ID or name..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500"
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
            </div>

            {/* Select All / Deselect All Toggle */}
            <div className="px-3 py-2 border-b border-gray-200 bg-gray-50">
              <button
                type="button"
                onClick={handleToggleSelectAll}
                disabled={isLoading || filteredCustomers.length === 0}
                className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                  allFilteredSelected
                    ? 'text-gray-600 bg-gray-100 hover:bg-gray-200'
                    : 'text-blue-600 bg-blue-50 hover:bg-blue-100'
                }`}
              >
                {allFilteredSelected ? 'Deselect All' : 'Select All'}
              </button>
            </div>

            {/* Customer list */}
            <div className="overflow-y-auto max-h-60">
              {isLoading ? (
                <div className="p-4 text-center text-sm text-gray-500">Loading customers...</div>
              ) : filteredCustomers.length === 0 ? (
                <div className="p-4 text-center text-sm text-gray-500">
                  {searchTerm ? 'No customers found' : 'No customers available'}
                </div>
              ) : (
                filteredCustomers.map(customer => {
                  const isSelected = selectedCustomerIds.includes(customer._id);
                  return (
                    <div
                      key={customer._id}
                      onClick={() => handleToggle(customer._id)}
                      className={`flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-gray-50 transition-colors ${
                        isSelected ? 'bg-blue-50' : ''
                      }`}
                    >
                      <div className={`flex-shrink-0 w-5 h-5 rounded border-2 flex items-center justify-center ${
                        isSelected
                          ? 'bg-blue-600 border-blue-600'
                          : 'border-gray-300'
                      }`}>
                        {isSelected && <Check size={14} className="text-white" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-[12px] text-gray-900">
                        {customer.companyName}
                        </div>
                        {customer.companyName && (
                          <div className="text-[10px] text-gray-600 mt-0.5">
                            {customer.code} - {customer.name}
                            </div>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        )}
        </div>
        
        {/* Add New Customer Button */}
        {onAddNewCustomer && (
          <button
            type="button"
            onClick={onAddNewCustomer}
            disabled={disabled}
            className="flex items-center gap-2 px-4 py-3 h-12 text-sm font-medium text-white bg-brand-600 hover:bg-brand-700 rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
          >
            <Plus size={18} />
            Add New Customer
          </button>
        )}
      </div>

      {error && (
        <p className="flex items-center gap-1 text-sm text-red-600">
          <span className="w-4 h-4 flex-shrink-0">⚠</span>
          {error}
        </p>
      )}
      
      {selectedCustomerIds.length > 0 && !error && (
        <p className="text-sm text-gray-500">
          {selectedCustomerIds.length} customer{selectedCustomerIds.length !== 1 ? 's' : ''} selected
        </p>
      )}
    </div>
  );
};

export default CustomerMultiSelect;
